import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { coachOverview, coachGrade, coachLandingAudit } from '../utils/api';
import { Sparkles, CheckCircle2, AlertTriangle, XCircle, Lock, ClipboardCheck, Globe } from 'lucide-react';

const StatusIcon = ({ status }) => {
  if (status === 'pass') return <CheckCircle2 size={18} className="text-lime shrink-0 mt-0.5" />;
  if (status === 'warn') return <AlertTriangle size={18} className="text-orange shrink-0 mt-0.5" />;
  return <XCircle size={18} className="text-red-400 shrink-0 mt-0.5" />;
};

const ScoreRing = ({ score, verdict }) => {
  const color = score >= 85 ? '#B7F04C' : score >= 60 ? '#E8731A' : '#f87171';
  return (
    <div className="flex items-center gap-4">
      <div className="relative w-24 h-24 shrink-0">
        <svg viewBox="0 0 100 100" className="w-24 h-24 -rotate-90">
          <circle cx="50" cy="50" r="42" fill="none" stroke="#1f2937" strokeWidth="10" />
          <circle cx="50" cy="50" r="42" fill="none" stroke={color} strokeWidth="10"
            strokeDasharray={`${(score / 100) * 264} 264`} strokeLinecap="round" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-black text-white">{score}</span>
        </div>
      </div>
      <div>
        <p className="text-xl font-black" style={{ color }}>{verdict}</p>
        <p className="text-xs text-muted">Scored 0–100 from your real campaign data</p>
      </div>
    </div>
  );
};

const AuditResult = ({ result, testId }) => (
  <div className="card" data-testid={testId}>
    <div className="flex flex-wrap items-center justify-between gap-4 mb-5 pb-5 border-b border-divider">
      <ScoreRing score={result.score} verdict={result.verdict} />
      <p className="text-xs text-muted max-w-[220px]">Data sources: {result.data_sources.join(', ')}</p>
    </div>
    <div className="space-y-3">
      {result.findings.map((f, i) => (
        <div key={i} className="flex gap-3 p-3 rounded-lg bg-black border border-divider" data-testid={`${testId}-finding-${i}`}>
          <StatusIcon status={f.status} />
          <div>
            <p className="text-sm font-bold text-white">{f.label}</p>
            <p className="text-sm text-light-gray leading-relaxed">{f.detail}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

export default function AICoach() {
  const [searchParams] = useSearchParams();
  const [overview, setOverview] = useState(null);
  const [campaignId, setCampaignId] = useState(searchParams.get('campaign') || '');
  const [grade, setGrade] = useState(null);
  const [landing, setLanding] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    coachOverview().then(({ data }) => setOverview(data)).catch(() => {});
  }, []);

  const runAudit = useCallback(async (type) => {
    if (!campaignId) { setError('Pick a campaign first.'); return; }
    setBusy(type);
    setError('');
    try {
      if (type === 'grade') {
        const { data } = await coachGrade(campaignId);
        setGrade(data);
      } else {
        const { data } = await coachLandingAudit(campaignId);
        setLanding(data);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Audit failed — try again.');
    } finally {
      setBusy('');
    }
  }, [campaignId]);

  useEffect(() => {
    if (searchParams.get('campaign') && overview) runAudit('grade');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview]);

  return (
    <Layout variant="light">
      <div className="max-w-6xl mx-auto px-6 py-8" data-testid="ai-coach-page">
        <h1 className="text-4xl font-black text-white mb-2 flex items-center gap-3">
          <Sparkles className="text-lime" size={32} /> AI Coach
        </h1>
        <p className="text-light-gray mb-2">Honest audits of your setup — scored only from real data already in GritClicks. No data, no score.</p>
        <p className="text-xs text-muted mb-8">{overview?.note}</p>

        <div className="card mb-8" data-testid="coach-controls">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[280px]">
              <label className="text-sm text-light-gray block mb-2">Campaign to audit</label>
              <select
                value={campaignId}
                onChange={(e) => { setCampaignId(e.target.value); setGrade(null); setLanding(null); }}
                data-testid="coach-campaign-select"
                className="w-full px-4 py-3 bg-slate-blue border border-divider rounded-lg text-white focus:outline-none focus:border-lime"
              >
                <option value="">Choose a campaign you've worked on…</option>
                {(overview?.campaigns || []).map((c) => (
                  <option key={c.id} value={c.id}>{c.campaign_name} ({c.trade})</option>
                ))}
              </select>
              {overview && overview.campaigns.length === 0 && (
                <p className="text-xs text-muted mt-2">No campaigns with goals or deployments yet — open a campaign in the Vault and pick a goal first.</p>
              )}
            </div>
            <button onClick={() => runAudit('grade')} disabled={!!busy} data-testid="coach-run-grade-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-lime text-black font-bold text-sm hover:bg-lime/90 transition-colors disabled:opacity-50">
              <ClipboardCheck size={16} /> {busy === 'grade' ? 'Grading…' : 'Grade My Campaign'}
            </button>
            <button onClick={() => runAudit('landing')} disabled={!!busy} data-testid="coach-run-landing-btn"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border-2 border-lime text-lime font-bold text-sm hover:bg-lime hover:text-black transition-colors disabled:opacity-50">
              <Globe size={16} /> {busy === 'landing' ? 'Auditing…' : 'Audit My Landing Page'}
            </button>
          </div>
          {error && <p className="text-orange text-sm mt-3" data-testid="coach-error">{error}</p>}
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-10">
          {grade && <AuditResult result={grade} testId="coach-grade-result" />}
          {landing && <AuditResult result={landing} testId="coach-landing-result" />}
        </div>

        <h2 className="text-xl font-bold text-white mb-1">Platform Account Audits</h2>
        <p className="text-sm text-muted mb-4">These need a live platform connection — GritClicks won't fake a score without real account data.</p>
        <div className="grid md:grid-cols-2 gap-4" data-testid="coach-locked-audits">
          {(overview?.locked_audits || []).map((a) => (
            <div key={a.id} className="card opacity-80" data-testid={`locked-audit-${a.id}`}>
              <div className="flex items-center gap-2 mb-2">
                <Lock size={16} className="text-muted" />
                <h3 className="text-white font-bold">{a.label}</h3>
              </div>
              <p className="text-sm text-light-gray mb-3">{a.description}</p>
              <p className="text-xs text-muted mb-3">{a.unlock}</p>
              <Link to={a.connect_url} className="text-sm text-lime font-semibold hover:underline" data-testid={`locked-audit-${a.id}-connect`}>
                Go to Connections →
              </Link>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
